import React, { useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TonnageApi, { TonnageImportModel } from '../Tonnage/api/TonnageApi';
import Utility from '../../app/utility';
import './BulkImportTonnage.scss';

const BulkImportTonnage: React.FunctionComponent = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [importData, setImportData] = useState<TonnageImportModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isValidated, setIsValidated] = useState<boolean>(false);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setImportData([]);
            setIsValidated(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            Utility.Alert.Warning('Please select an Excel file');
            return;
        }

        setIsLoading(true);
        try {
            const response = await TonnageApi.ImportTonnageFromExcel(selectedFile);
            if (response.success && response.data) {
                setImportData(response.data);
                setIsValidated(false);
                Utility.Alert.Success(`Imported ${response.data.length} records`);
            } else {
                Utility.Alert.Error('Failed to import file');
            }
        } catch (error: any) {
            Utility.Alert.Error(error.response?.data?.message || 'Error importing file');
        } finally {
            setIsLoading(false);
        }
    };

    const handleValidate = async () => {
        if (importData.length === 0) {
            Utility.Alert.Warning('No data to validate');
            return;
        }

        setIsLoading(true);
        try {
            const response = await TonnageApi.ValidateImportData(importData);
            if (response.success && response.data) {
                setImportData(response.data);
                const hasErrors = response.data.some(x => x.validationErrors && x.validationErrors.length > 0);
                setIsValidated(!hasErrors);

                if (hasErrors) {
                    Utility.Alert.Warning('Please fix validation errors before saving');
                } else {
                    Utility.Alert.Success('All records validated successfully');
                }
            }
        } catch (error: any) {
            Utility.Alert.Error(error.response?.data?.message || 'Error validating data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!isValidated) {
            Utility.Alert.Warning('Please validate data before saving');
            return;
        }

        setIsLoading(true);
        try {
            const response = await TonnageApi.SaveImportData(importData);
            if (response.success) {
                Utility.Alert.Success(`Successfully updated ${response.data} tonnage records`);
                // Clear data
                setImportData([]);
                setSelectedFile(null);
                setIsValidated(false);
                // Reset file input
                const fileInput = document.getElementById('file-input') as HTMLInputElement;
                if (fileInput) fileInput.value = '';
            }
        } catch (error: any) {
            Utility.Alert.Error(error.response?.data?.message || 'Error saving data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldChange = (index: number, field: keyof TonnageImportModel, value: string) => {
        const newData = [...importData];
        (newData[index] as any)[field] = value;
        setImportData(newData);
        setIsValidated(false); // Reset validation when data changes
    };

    const handleDeleteRow = (index: number) => {
        const newData = importData.filter((_, i) => i !== index);
        setImportData(newData);
    };

    const handleClear = () => {
        setImportData([]);
        setSelectedFile(null);
        setIsValidated(false);
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
    };

    const hasError = (item: TonnageImportModel, field: string): boolean => {
        if (!item.validationErrors) return false;

        if (field === 'vesselName') {
            return item.tonnageId === '';
        } else if (field === 'openArea') {
            return item.portOpen === 0;
        } else if (field === 'dateOpenStart' || field === 'dateOpenEnd') {
            return item.validationErrors.some(e => e.toLowerCase().includes('date'));
        }

        return false;
    };

    const getErrorMessage = (item: TonnageImportModel, field: string): string => {
        if (!item.validationErrors || item.validationErrors.length === 0) return '';

        if (field === 'vesselName' && item.tonnageId === '') {
            return item.validationErrors.find(e => e.toLowerCase().includes('vessel')) || '';
        } else if (field === 'openArea' && item.portOpen === 0) {
            return item.validationErrors.find(e => e.toLowerCase().includes('area') || e.toLowerCase().includes('port')) || '';
        } else if (field === 'dateOpenStart' || field === 'dateOpenEnd') {
            return item.validationErrors.find(e => e.toLowerCase().includes('date')) || '';
        }

        return '';
    };

    return (
        <Box className="bulk-import-tonnage-container" sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Bulk Import Tonnage
            </Typography>

            {/* File Upload Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                        id="file-input"
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        type="file"
                        onChange={handleFileSelect}
                    />
                    <label htmlFor="file-input">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadFileIcon />}
                        >
                            Select Excel File
                        </Button>
                    </label>
                    {selectedFile && (
                        <Typography variant="body2">{selectedFile.name}</Typography>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        color={!selectedFile || isLoading ? "info" : "primary"}
                        disabled={!selectedFile || isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Upload & Import'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleClear}
                        startIcon={<ClearIcon />}
                        disabled={isLoading}
                    >
                        Clear
                    </Button>
                </Box>
            </Paper>

            {/* Action Buttons */}
            {importData.length > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleValidate}
                            disabled={isLoading}
                        >
                            Validate Data
                        </Button>
                        <Button
                            variant="contained"
                            color={!isValidated || isLoading ? 'info' : 'primary'}
                            onClick={handleSave}
                            disabled={!isValidated || isLoading}
                        >
                            Save to Database
                        </Button>
                        <Typography variant="body2" sx={{ ml: 'auto', alignSelf: 'center' }}>
                            {importData.length} records loaded
                        </Typography>
                    </Box>
                </Paper>
            )}

            {/* Data Preview Table */}
            {importData.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Vessel Name</strong></TableCell>
                                <TableCell><strong>Open Area</strong></TableCell>
                                <TableCell><strong>Date Open Start</strong></TableCell>
                                <TableCell><strong>Date Open End</strong></TableCell>
                                <TableCell><strong>Port Name Current</strong></TableCell>
                                <TableCell><strong>Date Start Current</strong></TableCell>
                                <TableCell><strong>Date End Current</strong></TableCell>
                                <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {importData.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={item.vesselName}
                                            onChange={(e) => handleFieldChange(index, 'vesselName', e.target.value)}
                                            error={hasError(item, 'vesselName')}
                                            helperText={getErrorMessage(item, 'vesselName')}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={item.openArea}
                                            onChange={(e) => handleFieldChange(index, 'openArea', e.target.value)}
                                            error={hasError(item, 'openArea')}
                                            helperText={getErrorMessage(item, 'openArea')}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            value={item.dateOpenStart?.startsWith('0001-01-01') ? '' : item.dateOpenStart?.split('T')[0] || ''}
                                            onChange={(e) => handleFieldChange(index, 'dateOpenStart', e.target.value)}
                                            error={hasError(item, 'dateOpenStart')}
                                            helperText={getErrorMessage(item, 'dateOpenStart')}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            value={item.dateOpenEnd?.startsWith('0001-01-01') ? '' : item.dateOpenEnd?.split('T')[0] || ''}
                                            onChange={(e) => handleFieldChange(index, 'dateOpenEnd', e.target.value)}
                                            error={hasError(item, 'dateOpenEnd')}
                                            helperText={getErrorMessage(item, 'dateOpenEnd')}
                                        />
                                    </TableCell>
                                    <TableCell>{item.portNameCurrent || '-'}</TableCell>
                                    <TableCell>
                                        {item.dateOpenStartCurrent ? new Date(item.dateOpenStartCurrent).toLocaleDateString() : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {item.dateOpenEndCurrent ? new Date(item.dateOpenEndCurrent).toLocaleDateString() : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDeleteRow(index)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Empty State */}
            {importData.length === 0 && !isLoading && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No data loaded. Please select and upload an Excel file to begin.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default BulkImportTonnage;
